import { Component, inject, signal, HostListener, ElementRef, computed, effect, untracked, OnInit, OnDestroy, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { AvatarModule } from 'primeng/avatar';
import { RippleModule } from 'primeng/ripple';
import { AuthService } from '../../../core/services/auth';
import { NotificacionService } from '../../../core/services/notificacion.service';
import { ConfiguracionService } from '../../../core/services/configuracion.service';
import { MarcaService } from '../../../core/services/marca.service';
import { MarcaLogo } from '../../../shared/components/marca-logo/marca-logo';
import { ObraActivaService } from '../../../core/services/obra-activa.service';
import { AvisosService } from '../../../core/services/avisos.service';
import { AvisoPortal, ETIQUETA_SEVERIDAD, ICONO_SEVERIDAD, tipoDeAviso } from '../../../core/models/aviso-portal.model';
import { rutaDestinoSegura } from '../../../core/navegacion/ruta-aviso';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterModule, AvatarModule, RippleModule, MarcaLogo],
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
})
export class Navbar implements OnInit, OnDestroy {
  private authService    = inject(AuthService);
  private router         = inject(Router);
  private elRef          = inject(ElementRef);
  private configSvc      = inject(ConfiguracionService);

  notifService   = inject(NotificacionService);
  obraActivaSvc  = inject(ObraActivaService);
  /** Los avisos nuevos (/api/avisos); los comunicados del módulo antiguo siguen en `notifService`. */
  avisos         = inject(AvisosService);
  private marca      = inject(MarcaService);
  sistemaNombre  = computed(() => this.marca.nombre());

  async ngOnInit(): Promise<void> {
    // La config global (paleta, nombre del sistema, logo) la carga el arranque de la app; solo se pide aquí si aquella carga no pudo (servidor caído).
    if (!this.configSvc.cargada) await this.configSvc.cargar();

    // HU-OBRA-11 (adaptada): conveniencia de UX, no gate — se carga en paralelo, sin
    // bloquear el resto del navbar si falla o tarda.
    this.obraActivaSvc.cargar();

    // Notificaciones reales del backend, con el módulo actual como contexto.
    // Se recargan al cambiar de módulo en la navegación.
    this.notifService.cargar(this.moduloActual());

    // Los avisos nuevos: el conteo cada 90 s solo con la pestaña visible, al cambiar de ruta (mínimo 30 s entre consultas) y la lista solo al abrir el panel.
    this.avisos.iniciar();

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        const modulo = this.moduloActual();
        if (modulo !== this.ultimoModulo) {
          this.ultimoModulo = modulo;
          this.notifService.cargar(modulo);
        }
      });
  }

  ngOnDestroy(): void {
    // Cierre de sesión (o cambio de layout): se apagan los temporizadores y se olvida lo de la persona anterior.
    this.avisos.detener();
  }

  // Primer segmento de la URL → módulo válido para el alcance 'modulo' del backend
  private static readonly MODULO_POR_RUTA: Record<string, string> = {
    'causas-laborales':     'causas-laborales',
    'presupuestos-legales': 'presupuestos-legales',
    tarjetas:     'tarjetas',
    soporte:      'soporte',
    admin:        'administracion',
    obras:        'obras',
    'empresas-obras': 'obras',
    zonificacion: 'obras',
    combustible:  'combustible',
    evaluacion:   'evaluacion',
    trabajadores: 'trabajadores',
    cuadrillas:   'cuadrillas',
    empresas:     'empresas',
    comercial:    'comercial',
    produccion:   'produccion',
    reportes:     'reportes',
  };
  private ultimoModulo: string | undefined;

  private moduloActual(): string | undefined {
    const segmento = this.router.url.split('?')[0].split('/').filter(Boolean)[0] ?? '';
    return Navbar.MODULO_POR_RUTA[segmento];
  }

  currentUser = this.authService.currentUser;
  menuOpen = signal(false);
  notifOpen = signal(false);
  avatarCargaError = signal(false);

  /** Las no leídas de las dos fuentes: los comunicados del módulo antiguo y los avisos nuevos. */
  noLeidas = computed(() => this.notifService.noLeidas() + this.avisos.noLeidas());
  /** Hay algo urgente sin leer en alguna de las dos fuentes. */
  hayUrgentes = computed(() => this.avisos.hayUrgentes() || this.notifService.notificacionesOrdenadas().some(n => n.urgente && !n.leida));
  /** El número del globo, con tope «99+». */
  globoTexto = computed(() => (this.noLeidas() > 99 ? '99+' : String(this.noLeidas())));
  /** Lo que lee un lector de pantalla en el botón de la campana: «Notificaciones, 3 sin leer». */
  etiquetaCampana = computed(() => {
    const n = this.noLeidas();
    if (n === 0) return 'Notificaciones';
    return `Notificaciones, ${n > 99 ? 'más de 99' : n} sin leer${this.hayUrgentes() ? ', hay urgentes' : ''}`;
  });
  /** Comunicados del módulo antiguo (ya vienen ordenados). */
  notificaciones = computed(() => this.notifService.notificacionesOrdenadas());
  /** Las últimas 20 notificaciones nuevas, que se piden al abrir el panel. */
  avisosPanel = this.avisos.panel;

  /** La región educada (aria-live): dice cuántas hay sin leer cuando el número cambia. */
  anuncio = signal('');
  private ultimoAnuncio: { n: number; urgentes: boolean } | null = null;
  private anunciarCambios = effect(() => {
    const n = this.noLeidas();
    const urgentes = this.hayUrgentes();
    untracked(() => {
      const previo = this.ultimoAnuncio;
      if (!previo && n === 0) { this.ultimoAnuncio = { n, urgentes }; return; }
      if (previo && previo.n === n && previo.urgentes === urgentes) return;
      this.ultimoAnuncio = { n, urgentes };
      this.anuncio.set(n === 0
        ? 'No tienes notificaciones sin leer.'
        : `Tienes ${n > 99 ? 'más de 99' : n} ${n === 1 ? 'notificación sin leer' : 'notificaciones sin leer'}${urgentes ? ', y hay urgentes' : ''}.`);
    });
  });

  private campana = viewChild<ElementRef<HTMLButtonElement>>('campana');
  private panelNotif = viewChild<ElementRef<HTMLElement>>('panelNotif');

  userInitials  = computed(() => {
    const user = this.currentUser();
    if (!user) return 'U';
    return user.nombre.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
  });

  userAvatarUrl = computed(() => this.currentUser()?.avatarUrl ?? null);

  private resetAvatarErrorOnUrlChange = effect(() => {
    this.userAvatarUrl();
    this.avatarCargaError.set(false);
  });

  toggleMenu(): void {
    this.cerrarNotif();
    this.menuOpen.update(v => !v);
  }

  toggleNotif(): void {
    this.menuOpen.set(false);
    if (this.notifOpen()) { this.cerrarNotif(true); return; }
    this.notifOpen.set(true);
    // Refrescar desde el backend al abrir la campana: los comunicados de siempre y las últimas 20 notificaciones nuevas.
    this.notifService.cargar(this.moduloActual());
    void this.avisos.cargarPanel();
    // El foco entra al panel (un lector de pantalla lo anuncia como «Notificaciones»); Escape lo cierra y lo devuelve a la campana.
    setTimeout(() => this.panelNotif()?.nativeElement.focus(), 0);
  }

  /** Cierra el panel; con `devolverFoco` el foco vuelve al botón de la campana. */
  cerrarNotif(devolverFoco = false): void {
    if (!this.notifOpen()) return;
    this.notifOpen.set(false);
    this.avisos.cerrarPanel();
    if (devolverFoco) setTimeout(() => this.campana()?.nativeElement.focus(), 0);
  }

  /** Escape con el foco en la campana o en el panel. */
  alPulsarEscape(event: Event): void {
    if (!this.notifOpen()) return;
    event.stopPropagation();
    this.cerrarNotif(true);
  }

  /** Si el foco sale del conjunto campana + panel (con Tab), el panel se cierra. */
  alPerderElFoco(event: FocusEvent): void {
    const hacia = event.relatedTarget as Node | null;
    if (hacia && !(event.currentTarget as HTMLElement).contains(hacia)) this.cerrarNotif();
  }

  goToProfile(): void {
    this.menuOpen.set(false);
    this.router.navigate(['/configuracion/perfil']);
  }

  goToTickets(): void {
    this.menuOpen.set(false);
    this.router.navigate(['/soporte/mis-tickets']);
  }

  logout(): void {
    this.menuOpen.set(false);
    this.authService.clearSession();
    this.router.navigate(['/auth/login']);
  }

  marcarLeida(id: number, event: Event): void {
    event.stopPropagation();
    this.notifService.marcarLeida(id);
  }

  marcarTodasLeidas(): void {
    // Cada fuente se marca por su propia ruta: los comunicados por /notificaciones y los avisos nuevos por /avisos.
    this.notifService.marcarTodasLeidas();
    if (this.avisos.noLeidas() > 0) this.avisos.leerTodas().catch(() => { /* el mensaje queda a la vista en el panel */ });
  }

  eliminarNotif(id: number, event: Event): void {
    event.stopPropagation();
    this.notifService.descartar(id);
  }

  irAccion(ruta: string): void {
    this.cerrarNotif();
    this.router.navigate([ruta]);
  }

  // ── Avisos nuevos (/api/avisos) ──────────────────────────────────────────────────────────────
  conDestino(a: AvisoPortal): boolean { return rutaDestinoSegura(a.ruta) !== null; }
  iconoAviso(a: AvisoPortal): string { return ICONO_SEVERIDAD[a.severidad]; }
  etiquetaSeveridad(a: AvisoPortal): string { return ETIQUETA_SEVERIDAD[a.severidad]; }
  etiquetaTipo(a: AvisoPortal): string { return tipoDeAviso(a.tipo).etiqueta; }
  tiempoAviso(a: AvisoPortal): string { return this.notifService.tiempoRelativo(new Date(a.creadoEn)); }

  /** Tocar un aviso: se marca como leído y se va a su pantalla (si la ruta es válida). */
  async abrirAviso(a: AvisoPortal): Promise<void> {
    this.cerrarNotif();
    await this.avisos.abrir(a);
  }

  leerAviso(a: AvisoPortal, event: Event): void {
    event.stopPropagation();
    this.avisos.leer(a).catch(() => { /* el mensaje queda en el panel */ });
    this.devolverFocoAlPanel();
  }

  archivarAviso(a: AvisoPortal, event: Event): void {
    event.stopPropagation();
    this.avisos.archivar(a).catch(() => { /* el mensaje queda en el panel */ });
    this.devolverFocoAlPanel();
  }

  reintentarPanel(): void {
    void this.avisos.cargarPanel();
  }

  /** Al archivar, el botón pulsado desaparece: el foco pasa al panel para no perder el lugar. */
  private devolverFocoAlPanel(): void {
    setTimeout(() => {
      const panel = this.panelNotif()?.nativeElement;
      if (panel && !panel.contains(document.activeElement)) panel.focus();
    }, 0);
  }

  iconoPorTipo(tipo: string): string {
    return ({
      danger:  'pi-exclamation-triangle',
      warning: 'pi-bell',
      success: 'pi-check-circle',
      info:    'pi-info-circle',
    } as Record<string, string>)[tipo] ?? 'pi-bell';
  }

  tiempoRelativo(fecha: Date): string {
    return this.notifService.tiempoRelativo(fecha);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.menuOpen.set(false);
      this.cerrarNotif();
    }
  }
}
